import React, { useRef } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { auth0Audience } from '../../api/API';
import { CourseGroupV2, CourseType } from '../../api/models/Course';
import { Student } from '../../api/models/Student';
import { Environment } from '../../Environment';
import { history } from '../../util/History';
import { Auth0Context, useAuth0 } from '../Auth0Provider';

export type StudentRecordProps = {
  // inputs
  /** getting student */
  isGettingStudent: boolean;
  /** the student object */
  student: Student | undefined;
  /** getting course groups */
  isGettingCourseGroups: boolean;
  /** the course group array for the academic overview */
  courseGroups: CourseGroupV2[];
  // outputs
  userPressedCourseDetails: (courseType: CourseType) => void;
};

export const studentRecordContainer = (Screen: React.ComponentType<StudentRecordProps>) => () => {
  const [student, setStudent] = React.useState<Student | undefined>(undefined);
  const [courseGroups, setCourseGroups] = React.useState<CourseGroupV2[]>([]);
  const [isGettingStudent, setIsGettingStudent] = React.useState(false);
  const [isGettingCourseGroups, setIsGettingCourseGroups] = React.useState(false);

  const { api } = Environment.current();
  const { isAuthenticated, getTokenSilently } = useAuth0() as Auth0Context;
  const { id: studentId }: { id: string } = useParams();
  const isMounted = useRef(true);

  // To prevent update on unmount
  React.useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  React.useEffect(() => {
    if (studentId && isAuthenticated) {
      (async () => {
        try {
          setIsGettingStudent(true);
          const token = await getTokenSilently({
            audience: auth0Audience,
            scope: 'admin:students',
          });

          const id = parseInt(studentId);
          const studentResult = await api.getStudentById(id, token || '');

          if (isMounted.current) {
            setIsGettingStudent(false);
            setStudent(studentResult);
          }
        } catch (e) {
          toast.error(e.message);
          setIsGettingStudent(false);
        }
      })();
    }
  }, [studentId, isAuthenticated]);

  React.useEffect(() => {
    if (studentId && isAuthenticated) {
      (async () => {
        try {
          setIsGettingCourseGroups(true);
          const token = await getTokenSilently({
            audience: auth0Audience,
            scope: 'admin:students',
          });

          const id = parseInt(studentId);
          const courseGroupListResult = await api.getStudentCourseGroups(id, token || '');

          if (isMounted.current) {
            setIsGettingCourseGroups(false);
            setCourseGroups(courseGroupListResult);
          }
        } catch (e) {
          toast.error(e.message);
          setIsGettingCourseGroups(false);
        }
      })();
    }
  }, [studentId, isAuthenticated]);

  return (
    <Screen
      student={student}
      courseGroups={courseGroups}
      isGettingStudent={isGettingStudent}
      isGettingCourseGroups={isGettingCourseGroups}
      userPressedCourseDetails={courseType => {
        history.push(`/student-record/${studentId}/${courseType.toLowerCase()}`);
      }}
    />
  );
};
